import type { BaseEntity } from './BaseEntity';

/**
 * F 490 — the reminder node.
 *
 * WHAT A REMINDER IS
 * ------------------
 * A reminder is a PROPERTY OF A DATE. Not of a page, not of a task, not of a
 * container. A date is a value; a value has no life of its own; a reminder
 * is the thing that hangs off one.
 *
 * So the reminder is its own NODE, and it points at the node that owns the
 * date through `parent_node_id`.
 *
 * WHY NOT A FIELD ON THE DATE'S OWNER
 * ----------------------------------
 * One node can own SEVERAL dates: a task has a due date and a scheduled
 * date; a database item has a date property per date-typed property. A
 * field on the owner would have to be named per date — `dueReminder`,
 * `scheduledReminder`, `datePropertyReminder` — and that set is open-ended.
 * A separate node with a `parent_node_id` scales to any number of dates on
 * any owner, and the escape valves ("skip this one", "stop repeating")
 * are per-node rather than per-owner.
 *
 * WHY `parent_node_id` AND NOT A PROPERTY ID ALONE
 * ------------------------------------------------
 * The reminder has to say WHICH date it belongs to, and that takes two
 * facts: the node that owns the date, and the date's own identity within
 * that node. `parent_node_id` is the first; `date_property_id` is the
 * second, and it is optional because some dates have no property id (a
 * task's `due` date is a FIELD, not a property).
 *
 * THE REPEAT MODEL
 * ----------------
 * A reminder fires once, then schedules the next. `next_fire_at` is the
 * COMPUTED moment of the next firing and is the only field the scheduler
 * reads. `scheduled_at` is the moment the operator asked for; it is kept so
 * the original intent survives a change of repeat.
 *
 * `repeat_on` and `repeat_time` are the two axes of a weekly/monthly rule:
 * `repeat_on` is a weekday name or a day-of-month, `repeat_time` is
 * `HH:MM`. Both are optional — a daily reminder needs neither.
 *
 * THE STATES
 * ----------
 *   scheduled — waiting for `next_fire_at`. This is the NORMAL state of a
 *               live reminder, whether it has fired before or not.
 *   fired     — it has fired at least once, and its next occurrence is
 *               still ahead. MEASURED: the scheduler writes 'scheduled'
 *               again after a firing, so 'fired' is a marker the app may
 *               set, not a state the schedule gets stuck in.
 *   skipped   — one occurrence was skipped ("skip this one"). It is still
 *               scheduled; the scheduler has already advanced `next_fire_at`.
 *   dismissed — it will not fire again ("stop repeating", or a one-shot that
 *               has fired, or one whose `until` has passed). THE ONLY
 *               TERMINAL STATE.
 *
 * `skipped` and `dismissed` are SEPARATE on purpose. "Skip this one" is an
 * escape valve for ONE occurrence; "stop repeating" ends the series. A
 * single state would have to encode both, and then "skip" would silently
 * end the series.
 *
 * AND THE SCHEDULE IS NOT GATED BY THE STATE
 * ------------------------------------------
 * MEASURED, and this was a real bug in the first cut: the scheduler
 * filtered on `state === 'scheduled'` while ALSO writing `state = 'fired'`
 * after a firing. So a DAILY reminder fired exactly ONCE and then sat
 * there forever, because the state it had just written was the state the
 * scheduler refused to fire.
 *
 * The two facts are now separate. `state` is the LIFECYCLE and
 * `dismissed` is its only terminal value; the SCHEDULE is gated by
 * `next_fire_at`, which is the field the whole feature is built around.
 */

/** How often a reminder repeats. `none` means it fires exactly once. */
export type ReminderRepeat = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';

/** The lifecycle of a reminder. */
export type ReminderState = 'scheduled' | 'fired' | 'skipped' | 'dismissed';

/** Every repeat value, for a runtime picker. */
export const REMINDER_REPEATS: readonly ReminderRepeat[] = [
  'none',
  'daily',
  'weekly',
  'monthly',
  'yearly',
] as const;

/** Every state value, for a runtime narrowing. */
export const REMINDER_STATES: readonly ReminderState[] = [
  'scheduled',
  'fired',
  'skipped',
  'dismissed',
] as const;

/**
 * The reminder node.
 *
 * It is a `BaseEntity` and it follows the codebase's OWN convention for how
 * a node declares its kind: NOT a `kind` field. MEASURED: `Task` has no
 * `kind` field, and `isTask` recognises a task by its `fcid`
 * (`fcid://task/<id>`) — see `src/types/models/Task.ts:191`. `Page` does the
 * same. The kind is carried by the `fcid` and by the storage-key namespace,
 * and the `EntityKind` union is the taxonomy those two agree with.
 *
 * So a reminder carries `fcid: 'fcid://reminder/<id>'`, and the union's new
 * `'reminder'` member is what makes that fcid's kind legal. The fcid's kind
 * segment satisfies `FCID_RE` (`/^fcid:\/\/([a-z][a-z0-9-]*)\/(.+)$/` in
 * `src/core/refs/resolve.ts`), because `reminder` is a lowercase word.
 *
 * It lives in the same taxonomy as every other node and can be written to
 * the graph under the `reminder:` key prefix like any other.
 */
export interface Reminder extends BaseEntity {
  /** The reminder's own id. `BaseEntity` requires it. */
  id: string;

  /** `fcid://reminder/<id>` — how the node's kind is declared. */
  fcid: string;

  /** ISO datetime — the first firing the operator asked for. */
  scheduled_at: string;

  /** How the series repeats. `none` fires once and stops. */
  repeat: ReminderRepeat;

  /** Weekday name or day-of-month, for weekly/monthly rules. Optional. */
  repeat_on?: string | null;

  /** `HH:MM`, local to the operator. Optional. */
  repeat_time?: string | null;

  /**
   * The COMPUTED next firing. ISO datetime.
   *
   * This is the ONLY field the scheduler reads: it scans for
   * `next_fire_at <= now` and `state === 'scheduled'`.
   */
  next_fire_at: string;

  /** The last moment the series may fire. Optional — no end. */
  until?: string | null;

  /** The node that owns the date this reminder belongs to. */
  parent_node_id: string;

  /**
   * Which date on that node, when the date is a property rather than a
   * field. Optional: a task's `due` date is a field, not a property.
   */
  date_property_id?: string | null;

  /** The lifecycle state. */
  state: ReminderState;

  /**
   * The text the notification carries — the reminder's own words, or the
   * title of whatever owns the date. The scheduler copies this into the
   * notification it writes.
   */
  text?: string | null;
}

/**
 * A notification the scheduler wrote when a reminder fired.
 *
 * It is NOT a separate EntityKind: the dispatch's bound is "Only add the
 * reminder kind", so a notification is a NODE in the `notification:`
 * namespace with the same shape as every other node, carrying `read` so the
 * bell can count the unread ones and the inbox can mark them read. Its
 * `fcid` carries the `notification` kind, which is a node kind rather than
 * an `EntityKind` union member — the same way `fcid://task/<id>` exists
 * without the union needing a `task` member for it to work.
 */
export interface Notification extends BaseEntity {
  /** The notification's id. `BaseEntity` requires it. */
  id: string;

  /** `fcid://notification/<id>` — how the node's kind is declared. */
  fcid: string;

  /** The notification's text. */
  text: string;

  /** The reminder that produced it. */
  reminder_id: string;

  /** The node that owns the date the reminder belongs to. */
  parent_node_id: string;

  /** ISO datetime — when it fired. */
  fired_at: string;

  /** True until the operator has seen it. The bell counts the unread ones. */
  read: boolean;
}

/** Narrowing guard: is this node a reminder? */
export function isReminder(node: unknown): node is Reminder {
  if (!node || typeof node !== 'object') return false;
  const n = node as Partial<Reminder>;
  return (
    typeof n.id === 'string' &&
    typeof n.fcid === 'string' &&
    n.fcid.startsWith('fcid://reminder/') &&
    typeof n.next_fire_at === 'string' &&
    typeof n.state === 'string' &&
    (REMINDER_STATES as readonly string[]).includes(n.state)
  );
}

/** Narrowing guard: is this node a notification? */
export function isNotification(node: unknown): node is Notification {
  if (!node || typeof node !== 'object') return false;
  const n = node as Partial<Notification>;
  return (
    typeof n.id === 'string' &&
    typeof n.fcid === 'string' &&
    n.fcid.startsWith('fcid://notification/') &&
    typeof n.fired_at === 'string'
  );
}

/**
 * The fcid for a reminder, following the codebase's convention that a node
 * declares its kind through its fcid (`fcid://task/<id>` for a task — see
 * `src/types/models/Task.ts:200`).
 */
export function reminderFcid(id: string): string {
  return `fcid://reminder/${id}`;
}

/**
 * Create a reminder node.
 *
 * The `next_fire_at` is COMPUTED here, not passed in: the whole scheduler
 * contract is "scan for `next_fire_at <= now`", and a reminder whose
 * `next_fire_at` was left unset would be invisible to it. So the factory
 * refuses to make a reminder it cannot schedule.
 *
 * `scheduled_at` is kept as the operator's intent even when `repeat` is
 * `none`, so the record says what was asked for rather than only what the
 * scheduler did with it.
 */
export function createReminder(args: {
  id: string;
  parentNodeId: string;
  scheduledAt: string;
  repeat?: ReminderRepeat;
  repeatOn?: string | null;
  repeatTime?: string | null;
  until?: string | null;
  datePropertyId?: string | null;
  text?: string | null;
  now?: Date;
}): Reminder {
  const now = args.now ?? new Date();
  const repeat: ReminderRepeat = args.repeat ?? 'none';
  const scheduled = new Date(args.scheduledAt);
  if (Number.isNaN(scheduled.getTime())) {
    throw new Error(`createReminder: scheduledAt is not a valid date: ${args.scheduledAt}`);
  }
  return {
    id: args.id,
    fcid: reminderFcid(args.id),
    scheduled_at: args.scheduledAt,
    repeat,
    repeat_on: args.repeatOn ?? null,
    repeat_time: args.repeatTime ?? null,
    // THE FIRST FIRING, NOT THE SECOND.
    //
    // MEASURED on production, and this bug made EVERY recurring reminder
    // skip its first firing: a daily reminder set for 01:22 was written
    // with `next_fire_at` at 01:22 TOMORROW, so the scheduler found
    // nothing due and the reminder never rang. The record's `updatedAt`
    // was still its CREATION time, which is how it was proved that the
    // scheduler never touched it — the scheduler had nothing to fire.
    //
    // `computeNextFireAt` computes the occurrence AFTER a firing, so using
    // it to seed a brand-new reminder skips one. The first firing is
    // `scheduled_at`; the scheduler advances from there.
    next_fire_at: scheduled.toISOString(),
    until: args.until ?? null,
    parent_node_id: args.parentNodeId,
    date_property_id: args.datePropertyId ?? null,
    state: 'scheduled',
    text: args.text ?? null,
    createdAt: now.toISOString(),
    updatedAt: now.toISOString(),
  };
}

/**
 * Compute the next firing of a reminder.
 *
 * MEASURED, NOT ASSUMED. Every rule here is a DATE RULE, and the two
 * traps are:
 *
 *   1. ADDING A DAY IS NOT ADDING 24 HOURS. Across a DST boundary, a
 *      local wall-clock time is a different number of hours later. So the
 *      daily rule sets the local HOUR and MINUTE, not a duration.
 *   2. MONTH END IS NOT A FIXED DAY. January 31 + 1 month is not February
 *      31 — it is the last day of February. So the monthly rule clamps to
 *      the last day of the target month.
 *
 * The function is pure and takes the "now" as an argument, so a test can
 * pin the rules against fixed instants instead of against the wall clock.
 */
export function computeNextFireAt(
  from: Date,
  repeat: ReminderRepeat,
  options?: { repeatOn?: string | null; repeatTime?: string | null },
): Date {
  const next = new Date(from.getTime());

  if (repeat === 'none') return next;

  // The time-of-day the operator chose, when they chose one. Falls back to
  // the time of the firing that just happened, so a daily reminder keeps
  // its time without the operator retyping it.
  const time = options?.repeatTime;
  const [hh, mm] = time && /^\d{2}:\d{2}$/.test(time)
    ? [Number(time.slice(0, 2)), Number(time.slice(3, 5))]
    : [from.getHours(), from.getMinutes()];

  switch (repeat) {
    case 'daily': {
      next.setDate(next.getDate() + 1);
      next.setHours(hh, mm, 0, 0);
      return next;
    }
    case 'weekly': {
      const day = options?.repeatOn?.trim().toLowerCase();
      const names = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const target = names.indexOf(day ?? '');
      if (target < 0) {
        // No weekday named: a week is seven days. The fallback is measured,
        // not guessed at.
        next.setDate(next.getDate() + 7);
      } else {
        const current = next.getDay();
        let delta = target - current;
        if (delta <= 0) delta += 7;
        next.setDate(next.getDate() + delta);
      }
      next.setHours(hh, mm, 0, 0);
      return next;
    }
    case 'monthly': {
      const day = Number(options?.repeatOn);
      // The month we are moving INTO, as a 0-based index.
      const targetMonth = next.getMonth() + 1;
      // The days in THAT month. MEASURED, and this was wrong the first
      // time: `new Date(y, m + 1, 0)` where `m + 1` is the TARGET month
      // index gives the last day of the month BEFORE it — day 0 of a month
      // is the last day of the previous one. So for January → February the
      // expression returned 31 (January's length), and the clamp against 31
      // then set the 31st of February, which rolled straight on into March.
      // The correct expression takes the target month index itself.
      const daysInTarget = new Date(next.getFullYear(), targetMonth + 1, 0).getDate();
      const wanted = Number.isInteger(day) && day >= 1 && day <= 31 ? day : next.getDate();
      // Build the target from the FIRST of the target month, set the day
      // inside it, and copy the result across.
      //
      // MEASURED, and TWO overflows had to be killed to get here:
      //   1. `next.setMonth(targetMonth)` on 31 January lands on 3 MARCH,
      //      because February has no 31st.
      //   2. Copying the parts across in the order full-year → month → date
      //      had the SAME bug, because the month is still applied to the
      //      31st. The measured result was 28 MARCH — the right DAY and the
      //      wrong MONTH, which is the worst shape of wrong.
      //
      // So the target is built on day 1 (where no month length can bite),
      // the day is set inside it, and the result is copied as a whole.
      const target = new Date(next.getFullYear(), targetMonth, 1);
      target.setDate(Math.min(wanted, daysInTarget));
      next.setTime(target.getTime());
      next.setHours(hh, mm, 0, 0);
      return next;
    }
    case 'yearly': {
      next.setFullYear(next.getFullYear() + 1);
      next.setHours(hh, mm, 0, 0);
      return next;
    }
    default:
      return next;
  }
}
